import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Icon,
  Link,
  Spinner,
  Text as ChakraText,
  Tooltip,
  useToken,
  VStack,
} from '@chakra-ui/react'
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { MdOutlinePolicy } from 'react-icons/md'
import { NavLink, useRouteMatch } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import type { GetPathPolicyResponse } from '../../interfaces'
import { SideBar } from '../../layouts/Base'
import { SideBarItem } from '../../layouts/Base/SideBar'
import { UserContext } from '../../providers'
import PermissionGuard from '../PermissionGuard'
import SearchBar from '../SearchBar'
import Shadow from '../Shadow'

const PathPolicySideMenu = forwardRef<PathPolicySideMenuRef, unknown>(
  (_props, ref) => {
    const { t } = useTranslation()

    const { path } = useRouteMatch()

    const [searchQuery, setSearchQuery] = useState<string>('')

    const { user } = useContext(UserContext)

    const {
      get,
      data = [],
      loading,
    } = useFetch<GetPathPolicyResponse[]>({
      cachePolicy: CachePolicies.NO_CACHE,
    })

    const getPathPolicies = async (): Promise<void> => {
      if (user?.clientNumber) {
        await get(`/api/clients/${user.clientNumber}/path-policy`)
      }
    }

    useEffect(() => {
      void getPathPolicies()
    }, [user])

    useImperativeHandle(ref, () => ({
      getPathPolicies,
    }))

    const backgroundColor = useToken('colors', ['appPaleGrey']) as string

    if (user === null) {
      return null
    }

    const filteredData = data
      ? data.filter(x =>
          searchQuery
            ? x.name.toUpperCase().includes(searchQuery.toUpperCase())
            : true,
        )
      : []

    return (
      <PermissionGuard permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}>
        {(hasPermission: boolean) => (
          <SideBar
            heading={t('path_policy', 'Path policy')}
            subheading={data ? `(${data.length})` : ''}
            button={
              hasPermission ? (
                <Link
                  as={NavLink}
                  position="relative"
                  display="flex"
                  alignItems="center"
                  to={`${path}/new`}
                  _hover={{ textDecoration: 'none', outline: 'none' }}
                >
                  <Button>+{t('add_new', 'Add New')}</Button>
                </Link>
              ) : (
                <></>
              )
            }
          >
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              padding="1.5em 0.75em"
            />
            {!loading ? (
              data?.length ? (
                <Box
                  overflow="scroll"
                  h="100%"
                  position="relative"
                  padding="0em 0.75em 1.5em"
                  sx={{
                    /* Firefox */
                    scrollbarWidth: 'none',
                    /* IE 10+ */
                    msOverflowStyle: 'none',
                    /* Chrome / Edge / Safari */
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                  }}
                >
                  <Shadow backgroundColor={backgroundColor} />
                  <VStack spacing="1em" divider={<Divider />}>
                    {filteredData.length ? (
                      filteredData.map(x => {
                        return (
                          <SideBarItem
                            to={`${path}/${x.pathPolicyId}`}
                            key={x.pathPolicyId}
                          >
                            <Flex
                              flexDirection="column"
                              width="100%"
                              padding={2}
                              justifyContent="center"
                              overflow="hidden"
                            >
                              <Tooltip label={x.name}>
                                <ChakraText
                                  fontSize="md"
                                  fontWeight="medium"
                                  color={colors.appDarkIndigo}
                                  lineHeight="3rem"
                                  overflow="hidden"
                                  isTruncated
                                >
                                  {x.name}
                                </ChakraText>
                              </Tooltip>
                              <Tooltip label={x.description}>
                                <ChakraText
                                  width="full"
                                  fontSize="sm"
                                  lineHeight="base"
                                  color={colors.appBattleshipGrey}
                                  isTruncated
                                >
                                  {x.description}
                                </ChakraText>
                              </Tooltip>
                            </Flex>
                          </SideBarItem>
                        )
                      })
                    ) : (
                      <ChakraText color="appBattleshipGrey">
                        {t('no_matches_found', 'No matches found')}
                      </ChakraText>
                    )}
                  </VStack>
                </Box>
              ) : (
                <Flex
                  flexDirection="column"
                  textAlign="center"
                  justifyContent="center"
                  height="100%"
                  width="100%"
                >
                  <Icon
                    as={MdOutlinePolicy}
                    height="101px"
                    width="101px"
                    margin="0 auto"
                    marginBottom="1em"
                    color="appClearBlue"
                  />
                  <ChakraText
                    color={colors.appBattleshipGrey}
                    fontWeight="500"
                    fontSize="1em"
                    margin="0 auto"
                  >
                    {t('no_path_policy_added', 'No path policy added.')}

                    <br />
                    {t(
                      'add_path_policy_to_show_list',
                      'Please add a new path policy to show list.',
                    )}
                  </ChakraText>
                </Flex>
              )
            ) : (
              <Center height="100%">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="appPaleGrey"
                  color="appBlue"
                  size="xl"
                />
              </Center>
            )}
          </SideBar>
        )}
      </PermissionGuard>
    )
  },
)

export default PathPolicySideMenu

export interface PathPolicySideMenuRef {
  getPathPolicies(): Promise<void>
}
